import { Alert, Box, Chip, Divider, Radio, Skeleton, Stack, TextField, Typography } from '@mui/material';
import { FunctionComponent, useContext, useEffect, useState } from 'react';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';
import LibraryQuestionGroupService from '../../../shared/services/library-question-group/library-question-group.service';
import {
  ILibraryImportClient,
  ILibraryImportableGroup,
  ILibraryImportResult,
} from '../../../shared/services/library-question-group/library-question-group.model';
import { DialogWrapper } from '../../presentation-components/display-elements-components/dialog-wrapper';
import { BRAND_DISPLAY_FONT } from '../../pages/signup-login/auth/auth-branding';

// Pulling a survey a client already has into the library.
//
// The case this exists for is the partner who built questions and rules directly inside a
// client account before the Library existed. Importing copies the whole graph up AND links the
// client's rows to it, so the survey immediately reads as "pushed to that client, up to date"
// and the client is left with exactly the one copy they already had.
export const PartnerLibrarySurveyImportDialog: FunctionComponent<{
  open: boolean,
  closeHandler: () => void,
  onImported: (id: string) => void,
}> = ({ open, closeHandler, onImported }) => {
  const libraryService = new LibraryQuestionGroupService()
  const userNotificationsContext = useContext(UserNotificationsContext)

  const [clients, setClients] = useState<ILibraryImportClient[]>()
  const [selected, setSelected] = useState<{ clientId: string, group: ILibraryImportableGroup }>()
  const [name, setName] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [result, setResult] = useState<ILibraryImportResult>()

  useEffect(() => {
    if (!open) return
    setIsLoading(true)
    libraryService.getImportableGroups()
      .then(response => {
        setClients(response.clients)
        setIsLoading(false)
      })
      .catch(err => {
        console.log(err)
        setIsLoading(false)
        userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' })
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const close = () => {
    setSelected(undefined)
    setName('')
    setResult(undefined)
    closeHandler()
  }

  const selectGroup = (clientId: string, group: ILibraryImportableGroup) => {
    setSelected({ clientId, group })
    // The client's own name for it is almost always what the partner wants, but library names
    // must be unique across the partner, so it stays editable.
    setName(group.name)
  }

  const submitHandler = () => {
    if (result) { const id = result.libraryQuestionGroupId; close(); onImported(id); return }
    if (isSubmitting || !selected || !name.trim()) return
    setIsSubmitting(true)
    libraryService.importFromClient(selected.clientId, selected.group.id, name.trim())
      .then(response => {
        setResult(response)
        setIsSubmitting(false)
      })
      .catch(err => {
        setIsSubmitting(false)
        userNotificationsContext.openSnackbar({
          open: true,
          message: err?.response?.data?.message || 'There was an error, please try again',
          backgroundColor: 'text.primary',
        })
        console.log(err)
      })
  }

  const importable = (clients || []).filter(client => client.groups.length > 0)
  const anyAvailable = importable.some(client => client.groups.some(group => !group.alreadyLinked))

  return (
    <DialogWrapper
      open={open}
      onClose={close}
      title={result ? 'Import complete' : 'Import a survey from a client'}
      maxWidth='sm'
      cancelHandler={close}
      submitHandler={submitHandler}
      isSubmitting={isSubmitting}
      hideCancelButton={Boolean(result)}
      disableSaveButton={!result && (!selected || !name.trim())}
      submitButtonText={result ? 'Open it' : 'Import'}
      submitButtonLoadingText='Importing'
    >
      {isLoading && <Stack spacing={1}>
        {[0, 1, 2].map(row => <Skeleton key={row} variant='rectangular' height={32} />)}
      </Stack>}

      {!isLoading && !result && <>
        <Typography variant='body2' sx={{ mb: 2 }}>
          Copies a survey one of your clients already has into your library, with its questions,
          display conditions and emission rules, and puts you in charge of it. Their copy stays
          exactly where it is and stays linked, so it shows as already up to date.
        </Typography>

        {importable.length === 0 &&
          <Typography variant='body2'>None of your clients have a survey to import yet.</Typography>}

        {importable.length > 0 && !anyAvailable &&
          <Alert severity='info' sx={{ mb: 2 }}>
            Every survey your clients have already came from your library, so there is nothing left
            to import.
          </Alert>}

        <Stack spacing={2}>
          {importable.map(client => (
            <Box key={client.id}>
              <Typography variant='body2' sx={{ fontFamily: BRAND_DISPLAY_FONT, mb: 0.5 }}>
                {client.name}
              </Typography>
              <Divider sx={{ mb: 1 }} />
              <Stack spacing={0.5}>
                {client.groups.map(group => {
                  const isSelected = selected?.group.id === group.id
                  return (
                    <Stack
                      key={group.id}
                      direction='row'
                      alignItems='center'
                      spacing={1}
                      onClick={() => { if (!group.alreadyLinked) selectGroup(client.id, group) }}
                      sx={{
                        py: 0.5, px: 1, borderRadius: 1,
                        cursor: group.alreadyLinked ? 'default' : 'pointer',
                        opacity: group.alreadyLinked ? 0.5 : 1,
                        bgcolor: isSelected ? 'action.selected' : 'transparent',
                        '&:hover': { bgcolor: group.alreadyLinked ? 'transparent' : 'action.hover' },
                      }}
                    >
                      <Radio size='small' checked={isSelected} disabled={group.alreadyLinked} sx={{ p: 0.5 }} />
                      <Typography variant='body2' sx={{ flexGrow: 1 }}>{group.name}</Typography>
                      {group.alreadyLinked
                        ? <Chip size='small' label='Already in your library' />
                        : <>
                          <Chip size='small' label={`${group.questionCount} question${group.questionCount === 1 ? '' : 's'}`} />
                          <Chip size='small' label={`${group.ruleCount} rule${group.ruleCount === 1 ? '' : 's'}`} />
                        </>}
                    </Stack>
                  )
                })}
              </Stack>
            </Box>
          ))}
        </Stack>

        {selected && <Box sx={{ mt: 2 }}>
          <TextField
            label='Name it in your library'
            size='small'
            value={name}
            onChange={e => setName(e.target.value)}
            fullWidth
          />
        </Box>}
      </>}

      {result && <Stack spacing={1}>
        <Alert severity='success'>
          Imported {result.itemsImported} question{result.itemsImported === 1 ? '' : 's'} and{' '}
          {result.rulesImported} rule{result.rulesImported === 1 ? '' : 's'}.
        </Alert>
        {result.questionsReused > 0 && <Typography variant='body2'>
          {result.questionsReused} question{result.questionsReused === 1 ? ' was' : 's were'} already
          in your library and {result.questionsReused === 1 ? 'was' : 'were'} reused rather than copied again.
        </Typography>}
        <Typography variant='body2'>
          The client's copy is now linked to this library survey and shows as up to date. Changes you
          make here reach them the next time you push.
        </Typography>
        {result.surveysLeftAlone > 0 && <Typography variant='body2' sx={{ color: 'text.secondary' }}>
          They had {result.surveysLeftAlone + 1} surveys using these questions. The earliest is the
          one this library survey will keep updated.
        </Typography>}
      </Stack>}
    </DialogWrapper>
  )
}
